import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";

const currentUrl = window.location.href;
const firstSlashIndex = currentUrl.indexOf("/", currentUrl.indexOf("//") + 2);
const baseUrl =
  firstSlashIndex !== -1
    ? currentUrl.substring(0, firstSlashIndex)
    : currentUrl;

switch (baseUrl) {
  case "http://localhost:3000":
  case "http://localhost:9001":
  case "http://localhost:9002":
    console.log("Local Server");
    axios.defaults.baseURL = "http://localhost:8000";
    break;
  case "http://10.36.3.2:9001":
    console.log("Staging Server");
    axios.defaults.baseURL = "http://10.36.3.2:9000";
    break;
  case "https://vetum.ampnetwork.com.np":
    console.log("Deployed Server");
    axios.defaults.baseURL = "https://vetumnabkend.ampnetwork.com.np";
    break;
  default:
    console.log("Unknown Server");
    break;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: axios.defaults.baseURL,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      headers.set("accesstoken", token);
    }
    return headers;
  },
});

// Mirrors the axios interceptor in App.js: on a 401, try refreshing the
// access token once via the stored refresh token, then retry the original
// query. If refresh fails, clear auth state and send the user to login.
let isRefreshingToken = false;
let pendingResolvers = [];

const baseQueryWithReauth = async (args, apiObj, extraOptions) => {
  let result = await rawBaseQuery(args, apiObj, extraOptions);

  if (result.error && result.error.status === 401) {
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken) {
      localStorage.removeItem("user");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      window.location.href = "/auth/login";
      return result;
    }

    if (!isRefreshingToken) {
      isRefreshingToken = true;
      const refreshResult = await rawBaseQuery(
        { url: "/auth/admin/refresh", method: "POST", body: { refreshToken } },
        apiObj,
        extraOptions
      );
      isRefreshingToken = false;

      if (refreshResult.data) {
        localStorage.setItem("accessToken", refreshResult.data.accessToken);
        localStorage.setItem("refreshToken", refreshResult.data.refreshToken);
        pendingResolvers.forEach((resolve) => resolve());
        pendingResolvers = [];
        result = await rawBaseQuery(args, apiObj, extraOptions);
      } else {
        pendingResolvers = [];
        localStorage.removeItem("user");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        window.location.href = "/auth/login";
      }
    } else {
      // Another query is already refreshing — wait for it, then retry.
      await new Promise((resolve) => pendingResolvers.push(resolve));
      result = await rawBaseQuery(args, apiObj, extraOptions);
    }
  }

  return result;
};

export const api = createApi({
  baseQuery: baseQueryWithReauth,
  reducerPath: "adminApi",
  tagTypes: [
    "Admin",
    "Admins",
    "Roles",
    "Permissions",
    "Customers",
    "Transactions",
    "Dashboard",
    "Geography",
    "Sales",
    "Performance",
    "Status",
    "Package",
    "Event",
    "Interest",
    "Category",
    "Notification",
    "Meetup",
    "Wallet",
    "Profession",
    "Feedback",
    "UserActivity",
    "Post",
    "Rating",
    "SupportChat",
    "weightCalc",
    "Withdrawals",
    "CompanyWallet",
  ],

  endpoints: (build) => ({
    getUser: build.query({
      query: (id) => ``,
      providesTags: ["User"],
    }),
    getUserDetails: build.query({
      query: (userId) => ({
        url: "/user/singleUser",
        method: "GET",
        params: { userId },
      }),
      providesTags: ["Customers"],
    }),
    getUserMeetups: build.query({
      query: (userId) => ({
        url: "/meetup/list",
        method: "GET",
        params: { userId },
      }),
      providesTags: ["Meetup"],
    }),
    getUserWalletStatement: build.query({
      query: (userId) => ({
        url: "/wallet/statement/list",
        method: "GET",
        params: { userId },
      }),
      providesTags: ["Wallet"],
    }),
    getAdmins: build.query({
      query: () => "admin/list",
      providesTags: ["Admins"],
      withCredentials: true,
    }),
    getRoles: build.query({
      query: () => "role/list",
      providesTags: ["Roles"],
    }),
    getPermissions: build.query({
      query: () => "permission/list",
      providesTags: ["Permissions"],
    }),
    getDashboard: build.query({
      query: () => "dashboard",
      providesTags: ["Dashboard"],
    }),
    getCustomers: build.query({
      query: (params) => ({
        url: "/user/list",
        method: "GET",
        params,
      }),
      providesTags: ["Customers"],
    }),
    getTransactions: build.query({
      query: ({ page, pageSize, sort, search }) => ({
        url: "/transaction/list",
        method: "GET",
        params: { page, pageSize, sort, search },
      }),
      providesTags: ["Transactions"],
    }),
    getGeography: build.query({
      query: () => "client/geography",
      providesTags: ["Geography"],
    }),
    getSales: build.query({
      query: () => "sales/sales",
      providesTags: ["Sales"],
    }),
    getUserPerformance: build.query({
      query: (id) => `management/performance/${id}`,
      providesTags: ["Performance"],
    }),
    getWithdrawals: build.query({
      query: (status) => ({
        url: "/wallet/withdraw/list",
        method: "GET",
        params: status ? { status } : undefined,
      }),
      providesTags: ["Withdrawals"],
    }),
    verifyWithdrawal: build.mutation({
      query: (body) => ({
        url: "/wallet/withdraw/verify",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Withdrawals", "Wallet", "CompanyWallet"],
    }),
    getCompanyWallet: build.query({
      query: () => "/companyWallet/single",
      providesTags: ["CompanyWallet"],
    }),
    getCompanyWalletStatements: build.query({
      query: () => ({
        url: "/wallet/statement/list",
        method: "GET",
        params: { companyId: "BHETAMNATA_WALLET" },
      }),
      providesTags: ["CompanyWallet"],
    }),
    postUpdateStatus: build.mutation({
      query: ({ Id, tag, isActive }) => ({
        url: `/status/changeStatus`,
        method: "POST",
        body: { Id, tag, isActive },
      }),
      invalidatesTags: ["Status"],
      responseTransform(response) {
        return response.data;
      },
    }),
    getPackage: build.query({
      query: () => "package/list",
      providesTags: ["Package"],
    }),
    getProfession: build.query({
      query: () => "profession/list",
      // method: "GET",
      providesTags: ["Profession"],
    }),
    getEvent: build.query({
      query: () => "event/list",
      providesTags: ["Event"],
    }),
    getCategory: build.query({
      query: () => "category/list",
      providesTags: ["Category"],
      // responseTransform(response) {
      //   return response.Data;
      // },
    }),
    getNotification: build.query({
      query: () => "notification/list",
      providesTags: ["Notification"],
    }),
    changeNotificationStatus: build.mutation({
      query: ({ notificationId, status }) => ({
        url: "/notification/status",
        method: "POST",
        body: { notificationId, status },
      }),
      invalidatesTags: ["Notification"],
    }),
    getMeetupList: build.query({
      query: () => "meetup/list",
      providesTags: ["Meetup"],
    }),
    getFeedbackList: build.query({
      query: () => "feedback/list",
      providesTags: ["Feedback"],
    }),
    getWeightCalcList: build.query({
      query: () => "weightCalc/list",
      providesTags: ["WeightCalc"],
    }),
    getWalletList: build.query({
      query: () => "wallet/list",
      providesTags: ["Wallet"],
    }),
    getUserActivity: build.query({
      query: () => "userActivity/list",
      providesTags: ["UserActivity"],
    }),
    getPostList: build.query({
      query: () => "post/list",
      providesTags: ["Post"],
    }),
    updatePost: build.mutation({
      query: ({ postId, image, video, ...fields }) => {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value);
          }
        });
        if (image) formData.append("image", image);
        if (video) formData.append("video", video);
        return {
          url: "/post/update",
          method: "PUT",
          params: { postId },
          body: formData,
        };
      },
      invalidatesTags: ["Post"],
    }),
    deletePost: build.mutation({
      query: ({ postId, tag }) => ({
        url: "/post/delete",
        method: "POST",
        body: { postId, tag },
      }),
      invalidatesTags: ["Post"],
    }),
    getRatingList: build.query({
      query: () => "ratingAndFeedback/adminList",
      providesTags: ["Rating"],
    }),
    getSupportInbox: build.query({
      query: () => "supportChat/inbox",
      providesTags: ["SupportChat"],
    }),
    getSupportHistory: build.query({
      query: (userId) => ({
        url: "/supportChat/history",
        method: "GET",
        params: { userId },
      }),
      providesTags: ["SupportChat"],
    }),
    sendSupportMessage: build.mutation({
      query: (body) => ({
        url: "/supportChat/send",
        method: "POST",
        body,
      }),
      invalidatesTags: ["SupportChat"],
    }),
    getInterest: build.query({
      query: () => "interest/list",
      method: "GET",
      providesTags: ["Interest"],
    }),
  }),
});
// query: (uid, tag) => `/status/change?uid=${uid}&tag=${tag}`,

export const {
  useGetUserQuery,
  useGetUserDetailsQuery,
  useGetUserMeetupsQuery,
  useGetUserWalletStatementQuery,
  useGetAdminsQuery,
  useGetEventQuery,
  useGetRolesQuery,
  useGetPermissionsQuery,
  useGetCustomersQuery,
  useGetTransactionsQuery,
  useGetGeographyQuery,
  useGetSalesQuery,
  useGetUserPerformanceQuery,
  useGetDashboardQuery,
  usePostUpdateStatusMutation,
  useGetPackageQuery,
  useGetProfessionQuery,
  useGetCategoryQuery,
  useGetInterestQuery,
  useGetNotificationQuery,
  useChangeNotificationStatusMutation,
  useGetMeetupListQuery,
  useGetFeedbackListQuery,
  useGetWeightCalcListQuery,
  useGetWalletListQuery,
  useGetUserActivityQuery,
  useGetPostListQuery,
  useUpdatePostMutation,
  useDeletePostMutation,
  useGetRatingListQuery,
  useGetSupportInboxQuery,
  useGetSupportHistoryQuery,
  useSendSupportMessageMutation,
  useGetWithdrawalsQuery,
  useVerifyWithdrawalMutation,
  useGetCompanyWalletQuery,
  useGetCompanyWalletStatementsQuery,
} = api;
